import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuickActionFAB from '../../components/ui/QuickActionFAB';
import CustomerList from './components/CustomerList';
import CustomerProfile from './components/CustomerProfile';
import ActivityTimeline from './components/ActivityTimeline';
import CustomerSearchBar from './components/CustomerSearchBar';
import CustomerFilters from './components/CustomerFilters';
import Icon from '../../components/AppIcon';

const CustomerDatabase = () => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    industry: '',
    region: '',
    customerType: '',
    status: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  // Mock customer data - in real app would come from API
  const [customers] = useState([
    {
      id: 1,
      company: 'Construcciones García SL',
      contactPerson: 'José García Martínez',
      email: 'jose.garcia@construccionesgarcia.es',
      phone: '+34 915 123 456',
      location: 'Madrid, España',
      industry: 'Construcción',
      region: 'Madrid',
      customerType: 'Empresa',
      status: 'Activo',
      relationshipStatus: 'Cliente Frecuente',
      creditLimit: '€50,000',
      paymentTerms: '30 días',
      totalOrders: 47,
      totalRevenue: '€185,420',
      lastActivity: '2025-07-10',
      billingAddress: {
        street: 'Calle Mayor 123',
        city: 'Madrid',
        postalCode: '28013',
        country: 'España'
      },
      deliveryAddress: {
        street: 'Polígono Industrial Sur, Nave 15',
        city: 'Madrid',
        postalCode: '28021',
        country: 'España'
      }
    },
    {
      id: 2,
      company: 'Electrónica Barcelona SAU',
      contactPerson: 'Maria López Vila',
      email: 'maria.lopez@electronicabcn.com',
      phone: '+34 933 456 789',
      location: 'Barcelona, España',
      industry: 'Electrónica',
      region: 'Cataluña',
      customerType: 'Corporación',
      status: 'Activo',
      relationshipStatus: 'Cliente Premium',
      creditLimit: '€100,000',
      paymentTerms: '15 días',
      totalOrders: 89,
      totalRevenue: '€432,150',
      lastActivity: '2025-07-11',
      billingAddress: {
        street: 'Passeig de Gràcia 85',
        city: 'Barcelona',
        postalCode: '08008',
        country: 'España'
      },
      deliveryAddress: {
        street: 'Zona Franca, Sector C, Nave 8',
        city: 'Barcelona',
        postalCode: '08040',
        country: 'España'
      }
    },
    {
      id: 3,
      company: 'Talleres Andaluces Unidos',
      contactPerson: 'Antonio Ruiz Herrera',
      email: 'antonio@talleresandaluces.es',
      phone: '+34 954 321 654',
      location: 'Sevilla, España',
      industry: 'Automoción',
      region: 'Andalucía',
      customerType: 'Empresa',
      status: 'Pendiente',
      relationshipStatus: 'Cliente Nuevo',
      creditLimit: '€25,000',
      paymentTerms: '30 días',
      totalOrders: 3,
      totalRevenue: '€12,850',
      lastActivity: '2025-07-09',
      billingAddress: {
        street: 'Avenida de la Constitución 45',
        city: 'Sevilla',
        postalCode: '41001',
        country: 'España'
      },
      deliveryAddress: {
        street: 'Polígono Industrial Norte, Calle A, 12',
        city: 'Sevilla',
        postalCode: '41016',
        country: 'España'
      }
    }
  ]);

  // Mock activity data
  const [activities] = useState([
    {
      id: 1,
      customerId: 1,
      type: 'quotation',
      title: 'Presupuesto generado',
      description: 'Presupuesto #Q-2025-0245 por €15,420',
      date: '2025-07-10',
      time: '14:30',
      status: 'completed'
    },
    {
      id: 2,
      customerId: 1,
      type: 'order',
      title: 'Pedido confirmado',
      description: 'Pedido #P-2025-0156 procesado correctamente',
      date: '2025-07-08',
      time: '10:15',
      status: 'completed'
    },
    {
      id: 3,
      customerId: 2,
      type: 'invoice',
      title: 'Factura emitida',
      description: 'Factura #F-2025-0892 por €25,650',
      date: '2025-07-11',
      time: '16:45',
      status: 'completed'
    },
    {
      id: 4,
      customerId: 2,
      type: 'support',
      title: 'Ticket de soporte',
      description: 'Consulta técnica sobre instalación resuelta',
      date: '2025-07-10',
      time: '09:20',
      status: 'resolved'
    }
  ]);

  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
      if (customers.length > 0) {
        setSelectedCustomer(customers[0]);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [customers]);

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilters = 
      (!activeFilters.industry || customer.industry === activeFilters.industry) &&
      (!activeFilters.region || customer.region === activeFilters.region) &&
      (!activeFilters.customerType || customer.customerType === activeFilters.customerType) &&
      (!activeFilters.status || customer.status === activeFilters.status);

    return matchesSearch && matchesFilters;
  });

  const customerActivities = selectedCustomer 
    ? activities.filter(activity => activity.customerId === selectedCustomer.id)
    : [];

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilterChange = (filters) => {
    setActiveFilters(filters);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="text-lg text-text-secondary">Cargando base de datos de clientes...</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-heading-semibold text-text-primary mb-2">
                  Base de Datos de Clientes
                </h1>
                <p className="text-text-secondary font-body-normal">
                  Gestión integral de información de clientes con controles de acceso basados en roles
                </p>
              </div>
              
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-body-medium rounded-md text-white bg-primary hover:bg-primary-600 transition-micro">
                  <Icon name="Plus" size={16} className="mr-2" />
                  Nuevo Cliente
                </button>
                <button className="inline-flex items-center px-4 py-2 border border-border text-sm font-body-medium rounded-md text-text-secondary bg-surface hover:bg-secondary-50 transition-micro">
                  <Icon name="Download" size={16} className="mr-2" />
                  Exportar
                </button>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            <CustomerSearchBar onSearch={handleSearch} />
            <CustomerFilters onFilterChange={handleFilterChange} />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Customer List - 4 columns on desktop */}
            <div className="lg:col-span-4">
              <CustomerList 
                customers={filteredCustomers}
                selectedCustomer={selectedCustomer}
                onCustomerSelect={handleCustomerSelect}
              />
            </div>

            {/* Customer Profile - 5 columns on desktop */}
            <div className="lg:col-span-5">
              {selectedCustomer ? (
                <CustomerProfile customer={selectedCustomer} />
              ) : (
                <div className="card p-8 text-center">
                  <Icon name="Users" size={48} className="mx-auto text-text-muted mb-4" />
                  <h3 className="text-lg font-heading-semibold text-text-secondary mb-2">
                    Selecciona un Cliente
                  </h3>
                  <p className="text-text-muted">
                    Elige un cliente de la lista para ver su información detallada
                  </p>
                </div>
              )}
            </div>

            {/* Activity Timeline - 3 columns on desktop */}
            <div className="lg:col-span-3">
              <ActivityTimeline 
                activities={customerActivities}
                customerName={selectedCustomer?.company}
              />
            </div>
          </div>

          {/* Stats Summary for Mobile */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:hidden">
            <div className="card p-4 text-center">
              <div className="text-2xl font-heading-semibold text-primary mb-1">
                {customers.length}
              </div>
              <div className="text-sm text-text-secondary">Total Clientes</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-heading-semibold text-success mb-1">
                {customers.filter(c => c.status === 'Activo').length}
              </div>
              <div className="text-sm text-text-secondary">Activos</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-heading-semibold text-warning mb-1">
                {customers.filter(c => c.relationshipStatus === 'Cliente Premium').length}
              </div>
              <div className="text-sm text-text-secondary">Premium</div>
            </div>
          </div>
        </div>
      </main>

      <QuickActionFAB />
    </div>
  );
};

export default CustomerDatabase;